import React, {FC, useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {assignCorporateUser, loadCorporateUsers, removeCorporateUser} from './Api'
import {ICorporateModel, ICorporateUserModel} from './CorporateModel'

type Props = {
  corporate: ICorporateModel | null
  onClose: () => void
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Firmaya bağlı kullanıcılar; mevcut bir kullanıcı e-posta ile firmaya bağlanır */
export const CorporateUsersModal: FC<Props> = ({corporate, onClose}) => {
  const [users, setUsers] = useState<ICorporateUserModel[]>([])
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [defaultCorporate, setDefaultCorporate] = useState(true)
  const [saving, setSaving] = useState(false)

  const fetchUsers = useCallback(async () => {
    if (!corporate?.id) return
    setLoading(true)
    try {
      setUsers((await loadCorporateUsers(corporate.id)).data.payload || [])
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Kullanıcılar yüklenirken hata oluştu'))
    } finally {
      setLoading(false)
    }
  }, [corporate])

  useEffect(() => {
    if (!corporate) return
    setEmail('')
    setDefaultCorporate(true)
    fetchUsers()
  }, [corporate, fetchUsers])

  const handleAssign = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!corporate?.id || !EMAIL_PATTERN.test(email.trim())) return
    setSaving(true)
    try {
      await assignCorporateUser(corporate.id, email.trim(), defaultCorporate)
      showSuccessToast('Kullanıcı firmaya bağlandı')
      setEmail('')
      await fetchUsers()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Kullanıcı bağlanamadı'))
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (user: ICorporateUserModel) => {
    if (!corporate?.id) return
    try {
      await removeCorporateUser(corporate.id, user.userId)
      showSuccessToast('Kullanıcı firmadan çıkarıldı')
      await fetchUsers()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Kullanıcı çıkarılamadı'))
    }
  }

  return (
    <Modal show={!!corporate} onHide={onClose} size='lg' dialogClassName='modal-dialog-centered'>
      <div className='modal-header'>
        <h5 className='modal-title'>{corporate?.name} · Kullanıcılar</h5>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>

      <div className='modal-body'>
        <form className='d-flex flex-wrap align-items-center gap-3 mb-8' onSubmit={handleAssign} noValidate>
          <input
            type='email'
            className='form-control flex-grow-1'
            style={{minWidth: 240, width: 'auto'}}
            placeholder='Kayıtlı kullanıcının e-posta adresi'
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <div className='form-check form-check-custom form-check-solid'>
            <input
              id='default-corporate'
              type='checkbox'
              className='form-check-input'
              checked={defaultCorporate}
              onChange={(event) => setDefaultCorporate(event.target.checked)}
            />
            <label className='form-check-label' htmlFor='default-corporate'>
              Varsayılan firması olsun
            </label>
          </div>
          <button
            type='submit'
            className='btn btn-primary'
            disabled={saving || !EMAIL_PATTERN.test(email.trim())}
          >
            {saving ? <span className='spinner-border spinner-border-sm' /> : 'Bağla'}
          </button>
        </form>

        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <span className='spinner-border text-primary' />
          </div>
        ) : users.length === 0 ? (
          <div className='text-center text-muted py-10'>
            Bu firmaya bağlı kullanıcı yok. Kullanıcılar oturum açabilmek için bir firmaya bağlı olmalıdır.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed gy-3'>
              <thead>
                <tr className='text-muted fw-bolder fs-7 text-uppercase'>
                  <th>Kullanıcı</th>
                  <th>E-posta</th>
                  <th></th>
                  <th className='text-end'></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.userId}>
                    <td className='fw-bold'>
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.username}
                    </td>
                    <td>{user.email}</td>
                    <td>
                      {user.defaultCorporate && (
                        <span className='badge badge-light-primary'>Varsayılan</span>
                      )}
                    </td>
                    <td className='text-end'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-danger'
                        onClick={() => handleRemove(user)}
                      >
                        <i className='bi bi-person-dash'></i>
                        Çıkar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  )
}
